import Link from 'next/link';
import { motion } from 'framer-motion';
import SeoHead from '@/components/SeoHead';
import { Star, Quote } from 'lucide-react';
import { STORE_TESTIMONIALS } from '@/lib/customerReviews';

export default function TestimonialsPage() {
  return (
    <>
      <SeoHead
        title="Customer Testimonials | Trendy Quest Collection"
        description="Read what our satisfied customers say about Trendy Quest Collection footwear. Real reviews about comfort, style, and quality from Nairobi and across Kenya."
        path="/testimonials"
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-[#fffdf7] py-16 sm:py-20 lg:py-24">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,250,235,0.8),rgba(255,247,214,0.4),rgba(18,18,18,0.1))]" />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mx-auto mb-6 inline-flex rounded-2xl bg-green px-5 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-white shadow-[0_10px_30px_rgba(20,68,37,0.28)]"
              >
                Customer Reviews
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.6 }}
                className="mb-6 text-4xl font-playfair font-bold text-[#111111] sm:text-5xl lg:text-6xl"
              >
                What Our Customers Say
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="mx-auto max-w-2xl text-lg text-[#47433d] sm:text-xl"
              >
                Real stories from satisfied customers across Kenya who trust Trendy Quest Collection for quality footwear that combines comfort, style, and value.
              </motion.p>
            </div>
          </div>
        </section>

        {/* Testimonials Grid */}
        <section className="py-16 sm:py-20 lg:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {STORE_TESTIMONIALS.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="relative overflow-hidden rounded-[2.2rem] border border-[#e8dcc3] bg-white p-6 shadow-[0_18px_50px_rgba(20,68,37,0.06)] backdrop-blur-sm sm:p-8"
                >
                  <div className="absolute -top-2 -right-2">
                    <Quote className="h-8 w-8 text-green/20" />
                  </div>

                  <div className="mb-4 flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        className={i < testimonial.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                      />
                    ))}
                  </div>

                  <blockquote className="mb-6 text-[#47433d] leading-relaxed">
                    &ldquo;{testimonial.text}&rdquo;
                  </blockquote>

                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 overflow-hidden rounded-full bg-gray-200">
                      {/* Placeholder for customer photo */}
                      <div className="flex h-full w-full items-center justify-center bg-green text-white text-sm font-bold">
                        {testimonial.name.split(' ').map(n => n[0]).join('')}
                      </div>
                    </div>
                    <div>
                      <div className="font-semibold text-[#111111]">{testimonial.name}</div>
                      <div className="text-sm text-[#6a665f]">{testimonial.location}</div>
                      <div className="text-xs text-green">{testimonial.product}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="bg-[#f6f3ee] py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-playfair font-bold text-[#111111] sm:text-4xl">
                Trusted by Customers Across Kenya
              </h2>
              <p className="mt-4 text-lg text-[#47433d]">
                Join thousands of satisfied customers who choose Trendy Quest for their footwear needs
              </p>
            </div>

            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                className="text-center"
              >
                <div className="text-4xl font-playfair font-bold text-green sm:text-5xl">500+</div>
                <div className="mt-2 text-[#47433d]">Happy Customers</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1, duration: 0.6 }}
                className="text-center"
              >
                <div className="text-4xl font-playfair font-bold text-green sm:text-5xl">4.9</div>
                <div className="mt-2 text-[#47433d]">Average Rating</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="text-center"
              >
                <div className="text-4xl font-playfair font-bold text-green sm:text-5xl">98%</div>
                <div className="mt-2 text-[#47433d]">Satisfaction Rate</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="text-center"
              >
                <div className="text-4xl font-playfair font-bold text-green sm:text-5xl">24/7</div>
                <div className="mt-2 text-[#47433d]">Customer Support</div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-20 lg:py-24">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="rounded-[1.35rem] border border-[#ebe3ce] bg-white/80 p-4 shadow-[0_18px_50px_rgba(20,68,37,0.06)] backdrop-blur-sm sm:rounded-[2.2rem] sm:p-12"
            >
              <h2 className="mb-4 text-3xl font-playfair font-bold text-[#111111] sm:text-4xl">
                Ready to Experience the Difference?
              </h2>
              <p className="mb-8 text-lg text-[#47433d]">
                Join our satisfied customers and discover why Trendy Quest Collection is Kenya&apos;s trusted footwear brand.
              </p>
              <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
                <Link
                  href="/products"
                  className="inline-flex w-full items-center justify-center rounded-full bg-green px-5 py-3 text-base font-semibold text-white transition hover:bg-[#24552d] sm:w-auto sm:px-10 sm:py-4 sm:text-lg"
                >
                  Shop Now
                </Link>
                <Link
                  href="/contact"
                  className="inline-flex w-full items-center justify-center rounded-full border-2 border-green px-5 py-3 text-base font-semibold text-green transition hover:bg-green hover:text-white sm:w-auto sm:px-10 sm:py-4 sm:text-lg"
                >
                  Contact Us
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
